import {
  Button,
  Card,
  InlineAlert,
  ProgressBar,
  Spinner,
} from '@design-systems/canvas-ui'
import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  downloadBulk,
  downloadBulkFileAPICall,
  fetchAllZipJobsSummary,
} from '../../helpers/bulkDownloadHelper'
import apiConfig from '../../config/apiConfig'
import {
  BetaAnalytics,
  trackBetaSearchAnalytics,
} from '../../analytics/betaSearch'
import { appendQueryParams } from '../../helpers/UrlHelper'

const DownloadManager = () => {
  const [downloadRequests, setDownloadRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [downloadLoading, setDownloadLoading] = useState(false)
  const { assethub: { downloadZipUrl = '' } = {} } = apiConfig || {}
  const { auth: authData = {} } = useSelector((state) => state)

  useEffect(() => {
    setLoading(true)
    fetchAllZipJobsSummary()
      .then((response) => {
        setDownloadRequests(response.data)
      })
      .catch((err) => {
        setError('Failed to fetch ZIP files')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  useEffect(() => {
    let pollInterval = null

    const shouldPoll = downloadRequests.some(
      (req) => req.status === 'PENDING' || req.status === 'IN_PROGRESS',
    )

    if (shouldPoll) {
      pollInterval = setInterval(() => {
        fetchAllZipJobsSummary()
          .then((response) => {
            setDownloadRequests(response.data)
          })
          .catch(() => {
            setError('Failed to fetch ZIP files')
          })
      }, 5000)
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval)
    }
  }, [downloadRequests])

  if (loading) {
    return <div>Loading ZIP files...</div>
  }
  if (error) {
    return (
      <InlineAlert
        heading="Unable to load ZIP files"
        variant="error"
        style={{ margin: '20px' }}
      >
        {error}
      </InlineAlert>
    )
  }
  if (downloadRequests.length === 0) {
    return <div style={{ margin: '20px' }}>No ZIP download requests found.</div>
  }

  const onClickZipDownload = async (downloadRequest = {}) => {
    const { artifact_info = {} } = downloadRequest || {}
    const { key = '' } = artifact_info || {}
    const downloadFileName = key.replace('zip-downloads/', '')
    const getArtifactDownloadUrl = `${downloadZipUrl}/${downloadFileName}`
    const downloadParams = {
      key: apiConfig?.key,
    }
    const downloadFilePath = appendQueryParams(
      getArtifactDownloadUrl,
      downloadParams,
    )
    setDownloadLoading(true)
    try {
      const blobResponse = await downloadBulkFileAPICall(downloadFilePath)
      if (blobResponse) {
        downloadBulk(blobResponse, downloadFileName)
        setDownloadLoading(false)
        trackBetaSearchAnalytics({
          auth: authData,
          trackingData: {
            eventName: BetaAnalytics.event.DOWNLOAD_ASSET_ZIP,
            eventType: BetaAnalytics.event.ASSET_USAGE,
            eventData: downloadRequest,
          },
        })
      }
    } catch {
      setDownloadLoading(false)
    }
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        padding: '20px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <h3>
        We’re zipping your files — this may take a few minutes depending on
        size.
      </h3>
      <div>
        {downloadLoading ? (
          <>
            <Spinner /> <span>Downloading zip...</span>
          </>
        ) : (
          ''
        )}
      </div>
      <div
        style={{
          height: '80vh',
          overflow: 'scroll',
          display: 'flex',
          gap: '20px',
          padding: '10px 0px',
          flexDirection: 'column',
        }}
      >
        {downloadRequests.map((reqItem, index) => {
          const {
            status = '',
            requested_at = '',
            download_type = '',
            artifact_info = {},
            failed_asset_count = Number(0),
            successful_assets_count = Number(0),
            total_asset_count = Number(0),
          } = reqItem
          const { key = '' } = artifact_info || {}
          const successPercentage =
            total_asset_count > 0
              ? Math.round((successful_assets_count / total_asset_count) * 100)
              : 0
          return (
            <Card
              key={index}
              style={{ padding: '20px', border: '1px solid black' }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                }}
              >
                <div style={{ fontSize: '16px', color: 'black' }}>
                  {key.replace('zip-downloads/', '') ||
                    `${requested_at}_${download_type}`}
                </div>
                <div>{successPercentage}%</div>
              </div>

              <ProgressBar
                percentComplete={
                  status === 'IN_PROGRESS' || status === 'PENDING'
                    ? 30
                    : successPercentage
                }
              />
              <div style={{ color: '#676B74' }}>
                {status === 'IN_PROGRESS' || status === 'PENDING' ? (
                  <span>ZIP creation in progress...</span>
                ) : status === 'COMPLETED' && failed_asset_count === 0 ? (
                  <span>ZIP creation complete!</span>
                ) : status === 'COMPLETED' && failed_asset_count > 0 ? (
                  <span>We had hit a roadblock!</span>
                ) : status === 'FAILED' ? (
                  <span>Error creating ZIP!</span>
                ) : null}
                {status === 'COMPLETED' && failed_asset_count > 0 ? (
                  <div style={{ paddingTop: '10px' }}>
                    <div style={{ fontSize: '16px', color: 'black' }}>
                      Partial Zip Created
                    </div>
                    <div>
                      ZIP with{' '}
                      <span style={{ fontSize: '16px', color: 'black' }}>
                        {successful_assets_count}/{total_asset_count}
                      </span>{' '}
                      assets is created & ready for download.
                    </div>
                  </div>
                ) : (
                  ''
                )}
              </div>

              {status === 'COMPLETED' && (
                <Button
                  variant="primary"
                  onClick={() => onClickZipDownload(reqItem)}
                  style={{
                    color: 'white',
                    backgroundColor: 'green',
                    marginTop: '10px',
                  }}
                >
                  Ready to Download
                </Button>
              )}
              {((status === 'COMPLETED' && failed_asset_count > 0) ||
                status === 'FAILED') && (
                <InlineAlert
                  heading="Some files could not be processed"
                  variant="error"
                  style={{ marginTop: '20px' }}
                >
                  Some files ({failed_asset_count}/{total_asset_count}) may be
                  corrupted & need attention.
                </InlineAlert>
              )}
            </Card>
          )
        })}
      </div>
    </div>
  )
}

export default DownloadManager
